import * as _ from "underscore";
import {
  WorksheetUpdate,
  WorksheetValuesDeleteRequest,
  WorksheetValuesRequest,
} from "@eave/shared/types/api/worksheets";

import APIClient from "../../../APIClient";
import { deepInstanceToWsList } from "./reducer";
import { IWorksheetState } from "./interfaces";
import { FIELD_TYPE, getFieldDef, getFieldNames, WorksheetName, WS_DEF, WS_NAME } from "./wsDefs";

export const getWsById = (wsList: IWorksheetState[], id: string): IWorksheetState =>
  _.findWhere(wsList, { id });

export const getWsByName = (wsList: IWorksheetState[], name: string): IWorksheetState => {
  const ws: any = _.where(wsList, { name });
  if (!ws.length) {
    return null;
  }
  if (ws.length > 1) {
    throw new Error(`getWsByName failed: more than one worksheet ${name}`);
  }
  return ws[0];
};

export const hasWsId = (wsList: IWorksheetState[], id: string): boolean => !!getWsById(wsList, id);

export const withoutWs = (wsList: IWorksheetState[], id: string | string[]): IWorksheetState[] => {
  const ids = _.isArray(id) ? id : [id];
  return _.reject(wsList, (ws: IWorksheetState) => _.contains(ids, ws.id));
};

export const getParentWs = (wsList: IWorksheetState[], childWsId: string): IWorksheetState => {
  const ws = _.filter(wsList, (ws: IWorksheetState) => {
    const fieldNames = getFieldNames(ws.name);
    for (let i = 0, l = fieldNames.length; i < l; i += 1) {
      const field = fieldNames[i];
      const fieldDef = getFieldDef(ws.name, field);
      if (fieldDef.type === FIELD_TYPE.WS_SLICE && _.contains(ws.instance[field], childWsId)) {
        return true;
      }
      if (fieldDef.type === FIELD_TYPE.WS && ws.instance[field] === childWsId) {
        return true;
      }
    }
    return false;
  });
  if (!ws.length) {
    return null;
  }
  if (ws.length > 1) {
    throw new Error(`getParentWsId failed: more than one parent for ${childWsId}`);
  }
  return ws[0];
};

export const fetchWorksheets = app => {
  return APIClient.get(`/worksheets/${app.loan_file_worksheet_id}`).then((resp: any) =>
    deepInstanceToWsList(app.id, WS_NAME.LOAN_FILE, resp.worksheet),
  );
};

export const prepareUpdatesForAPI = (updates): WorksheetUpdate[] => {
  return Object.keys(updates)
    .map(key => {
      if (key === "id" || key === "version") {
        return null;
      }
      return {
        name: key,
        value: updates[key],
      };
    })
    .filter(Boolean);
};

/**
 * Given an object representing a map of modified values, updates the value of a worksheet
 * @param  {number} applicationId - the id of the application
 * @param  {object} updatesMap - an object with keys representing worksheet names and values representing the new values
 * @param  {string} id - the id of the worksheet to update
 * @param  {string} version - the version of the worksheet to update
 */
export const updateWSValue = (applicationId, updatesMap, id, version) => {
  if (!updatesMap) {
    // no updates -- nothing to do.
    return Promise.resolve(null);
  }

  const updates = prepareUpdatesForAPI(updatesMap);
  const payload: WorksheetValuesRequest = {
    application_id: applicationId,
    version,
    updates,
  };

  return APIClient.post(`/worksheets/${id}`, payload);
};

/**
 * Given the id of a "parent" worksheet, deletes a child worksheet
 * @param  {string} id - the id of the parent worksheet
 * @param  {string} name - the name of the child worksheet (e.g. "reserve_requirements").
 * @param  {string} version - the version of the parent worksheet
 * @param  {number} index - the index of the child worksheet in the parent worksheet
 */
export const deleteWSValue = (id, name, version: string, index) => {
  const payload: WorksheetValuesDeleteRequest = {
    version,
    updates: [
      {
        name,
        action: "delete",
        index,
      },
    ],
  };
  return APIClient.post(`/worksheets/${id}`, payload);
};

export const updateLoanFileWorksheet = (loanFileWs, updates: WorksheetUpdate[]) => {
  const payload = {
    version: loanFileWs.instance.version,
    updates,
  };

  return APIClient.post(`/worksheets/${loanFileWs.id}`, payload);
};

export const attachChildToParentWS = (
  application_id,
  childWsId,
  parentWsId,
  wsName,
  parentWsVersion,
) => {
  return APIClient.post(`/worksheets/${parentWsId}`, {
    application_id,
    updates: [
      {
        name: wsName,
        value: childWsId,
      },
    ],
    version: parentWsVersion,
  });
};

export const getWorksheetsByName = (wsList: IWorksheetState[], name: string): IWorksheetState[] =>
  wsList.filter(ws => ws.name === name);
